import type { Building } from "building-types/building/buildingTypes";
import type {
  FlooringKeyAttributes,
  FlooringKAM,
  FlooringKAMOptions,
  FlooringKeyMeasures,
} from "building-types/dataSequences/builtin/FlooringKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  DOC_ENTITY_COLUMN_NAME,
  SortBy,
  aggregatorColumnNew,
  categoryColumnNew,
  configurableTableNew,
  identifierColumnNew,
  rowMetadataNew,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import type { RoomKBMData } from "./rooms";
import { getRoomsFromBuilding } from "./rooms";

// Attributes used in procure entityHash
export const DEFINES_FLOORING: (keyof FlooringKeyAttributes)[] = ["material"];

export function makeFlooringKAM(room: RoomKBMData): FlooringKAM {
  return {
    type: "FLOORING",
    attributes: {
      level: keyAttributeNew(room.level),
      roomName: keyAttributeNew(room.name),
      material: keyAttributeNew(room.floorMaterial),
      materialHash: keyAttributeNew(room.idMatHash),
      entityName: keyAttributeNew(`FM-${room.floorMaterial}`),
    },
    measures: {
      area: keyMeasureNew(room.area ?? 0, "SQ FT"),
      count: keyMeasureNew(1),
    },
    handles: [room.roomHandle],
  };
}

export const EMPTY_FLOORING_KAM: FlooringKAM = {
  type: "FLOORING",
  attributes: {
    level: keyAttributeNew(""),
    roomName: keyAttributeNew(""),
    material: keyAttributeNew(""),
    materialHash: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultFlooringKAMOptions(): FlooringKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
    },
  };
}

export const defaultFlooringKAMSort = (a: FlooringKAM, b: FlooringKAM) => {
  return a.attributes.level.value.localeCompare(b.attributes.level.value);
};

/** @deprecated - use the Building Query API instead */
export function buildingFlooringKAMs(building: Building, filter?: BuildingEntityFilter): FlooringKAM[] {
  const rooms = getRoomsFromBuilding(building);
  const result: FlooringKAM[] = [];
  for (const itemId of Object.keys(rooms)) {
    const roomKBM = rooms[itemId];
    if (filter) {
      const roomHandle = roomKBM.roomHandle;
      const room = roomHandle.type === "ROOM" ? building.rooms[roomHandle.id] : undefined;
      if (!room || !filter(room)) {
        continue;
      }
    }
    result.push(makeFlooringKAM(roomKBM));
  }
  return result;
}

export const FLOORING_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  FlooringKeyAttributes,
  FlooringKeyMeasures
> = {
  attributes: {
    materialHash: {
      hidden: true,
    },
    material: {
      header: "Floor Material",
    },
    entityName: {
      hidden: true,
    },
  },
};

// Marked for deprecation
export function makeProcureFlooringTable(building: Building): ConfigurableTable {
  const roomKBMEntries = buildingFlooringKAMs(building);

  return configurableTableNew<FlooringKAM, FlooringKAM>({
    iterables: roomKBMEntries,
    requirement: (x) => x,
    columns: [
      categoryColumnNew("Flooring"),
      stringColumnNew({
        name: "ID Mat Hash",
        fieldFunc: (counter) => counter.attributes.materialHash.value,
        hidden: true,
        useInDiff: true,
      }),
      stringColumnNew({
        name: "Level",
        fieldFunc: (counter) => counter.attributes.level.value,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Name",
        fieldFunc: (counter) => counter.attributes.roomName.value,
        sortBy: SortBy.None,
      }),
      stringColumnNew({
        name: "Floor Material",
        fieldFunc: (counter) => counter.attributes.material.value,
      }),
    ],
    aggregator: aggregatorColumnNew({
      name: "Area (SF)",
      aggregatorFunc: (counter) => counter.measures.area.value,
    }),
    identifiers: [
      identifierColumnNew({
        name: DOC_ENTITY_COLUMN_NAME,
        identifierFunc: (row) => row.handles.map(docEntityHandleHash).join(",") || "0",
      }),
    ],
    rowMetadata: rowMetadataNew({
      metadataFunc: (counter) => {
        const entityName = `FM-${counter.attributes.material.value}`;
        return { entityName, entityType: "FLOORING" };
      },
    }),
  });
}
